import _ from 'lodash';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';

import { makeStyles, useTheme } from '@mui/styles';

import SelectClasses, {
  MultiSelectStyles,
  MultiSelectStyles1,
} from './MultiSelectStyles';

import MultiSelectButtonWithStyles from './MultiSelectButton';
import {
  Dropdown,
  DropdownIndicator,
  MenuList,
  Option,
} from './MultiSelectComponentOverrides';
import YellowWarningIcon from '../../../_assets/svg/YellowWarning.svg';
import SimpleTooltip from '../../SimpleTooltip';

const useStyles = makeStyles(SelectClasses as any);

function MultiSelect(props: Readonly<any>) {
  const {
    isShowing = true,
    isMulti = true,
    disabled = false,
    values: propValues = [],
    components = {},
    label,
    name,
    required,
    labelClass,
    error,
    customStyle,
    buttonPlaceholder = 'Select',
    options = [],
    showErrorPlace = true,
  } = props;

  const classes = useStyles();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [values, setValues] = useState(propValues);
  const prevValuesRef = useRef(propValues);

  useEffect(() => {
    if (!_.isEqual(prevValuesRef.current, propValues)) {
      setValues(propValues);
    }
    prevValuesRef.current = propValues;
  }, [propValues]);

  const toggleOpen = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  /**
   * Dismissal closes; it does not toggle.
   *
   * <p>The popover is controlled by `isOpen`, so a dismiss wired to `toggleOpen` is a handler that
   * can reopen what it was asked to close -- any dismissal arriving while the menu is already on
   * its way shut flips it back open.
   */
  const handleClose = () => setIsOpen(false);

  const toggleAllHandler = () => props.toggleAllHandler();

  const onSelectChange = (nextValues: any) => {
    setValues(nextValues);

    if (typeof props.onChange === 'function') {
      props.onChange(name, nextValues);
    }

    if (!isMulti) {
      toggleOpen();
    }
  };

  if (!isShowing) {
    return null;
  }

  const state = { isOpen, values };

  let SelectedOption = Option;
  if (_.get(props, 'components.Option')) {
    SelectedOption = components.Option;
  }

  const isRequired = required ? { required: true } : {};

  return (
    <Fragment>
      {label && (
        <label className={`${classes.label} ${labelClass}`} htmlFor={name}>
          {label}
          {required ? (
            <i
              style={{
                color: '#f95f53',
                fontSize: '16px',
                lineHeight: '16px',
              }}
            >
              *
            </i>
          ) : (
            ''
          )}
        </label>
      )}
      <Dropdown
        id={name}
        isOpen={isOpen}
        onClose={handleClose}
        disabled={disabled}
        target={
          components.DropDownButton ? (
            React.createElement(components.DropDownButton, {
              fullWidth: true,
              toggleOpen,
              disabled,
              buttonPlaceholder,
              data: state,
              ...props,
            })
          ) : (
            <MultiSelectButtonWithStyles
              isOpen={isOpen}
              fullWidth
              toggleOpen={toggleOpen}
              disabled={disabled}
              classname={classes.select}
              error={error}
              customStyle={customStyle}
            >
              <span>{generateSelectedLabel(state, props)}</span>

              <ArrowDropDown className={classes.buttonDown} />
            </MultiSelectButtonWithStyles>
          )
        }
      >
        {isOpen ? (
          <Select
            autoFocus
            backspaceRemovesValue={false}
            components={{
              DropdownIndicator,
              Option: SelectedOption,
              MenuList,
              IndicatorSeparator: null,
            }}
            toggleAllHandler={toggleAllHandler}
            classes={classes}
            controlShouldRenderValue={false}
            hideSelectedOptions={false}
            isClearable={false}
            isMulti={isMulti}
            supportSelectAll={props.supportSelectAll}
            menuIsOpen
            onChange={onSelectChange}
            options={options}
            placeholder={props.placeholder}
            styles={
              !props.supportSelectAll
                ? MultiSelectStyles(theme)
                : MultiSelectStyles1(theme)
            }
            tabSelectsValue={false}
            value={values}
            {...isRequired}
            onFocus={props.onFocusHandler}
            onBlur={props.onBlurHandler}
            onMenuScrollToBottom={props.onMenuScrollToBottom}
            onMenuScrollToTop={props.onMenuScrollToTop}
            inputProps={
              props.inputProps ? { required: true } : { required: false }
            }
            onInputChange={props.inputChange}
            isLoading={props.isLoading}
            noOptionsMessage={props.noOptionsMessage}
          />
        ) : (
          <span />
        )}
      </Dropdown>

      {error && <span className={classes.errorText}>{error}</span>}

      {!error && showErrorPlace && (
        <span className={classes.noErrorText} aria-hidden>
          error ph
        </span>
      )}
    </Fragment>
  );
}

function generateSelectedLabel(
  state: any,
  { customStyle, buttonPlaceholder, customLabel }: any
) {
  if (customLabel) {
    return customLabel;
  }

  if (customStyle && Array.isArray(state.values)) {
    const str = state.values.map(({ label }: any) => label).join(', ');

    if (state.values.length === 1) {
      return str;
    }

    if (state.values.length > 1) {
      return (
        <SimpleTooltip title={str}>
          <YellowWarningIcon />
        </SimpleTooltip>
      );
    }
    return buttonPlaceholder;
  }

  if (Array.isArray(state.values)) {
    if (state.values.length > 0) {
      return `${state.values
        .map(({ label }: any, index: any) => (index < 2 ? label : ''))
        .slice(0, 2)
        .join(', ')}${state.values.length > 2 ? ', ...' : ''}`;
    }
  }

  if (typeof state.values === 'object' && !_.isEmpty(state.values)) {
    return state.values.label;
  }

  return buttonPlaceholder;
}

export default MultiSelect;
